import {
  useState, useRef, useCallback, useEffect,
} from 'react';
import { useCellHoverContent } from '../../services/table/cellHoverQueries';

const HOVER_OPEN_DELAY_MS = 400;
const HOVER_CLOSE_DELAY_MS = 120;

/**
 * @typedef {object} UseCellHoverPreviewReturn
 * @property {boolean} isOpen
 * @property {CellHoverContent} html
 * @property {boolean} isEmpty
 * @property {boolean} showCursorLoader
 * @property {Error | null} error
 * @property {() => void} onPopoverMouseLeave
 * @property {() => void} onPopoverMouseEnter
 * @property {(open: boolean) => void} onOpenChange - Sync close with Floating UI dismiss (Escape, etc.)
 * @property {() => void} dismissPreview - Close preview immediately (e.g. when direct edit opens)
 */

/**
 * Grid-level hover preview: delay, fetch (via TanStack Query), open/close.
 * Driven by the currently hovered cell (hoverUrl + isPointerOnCell) rather than
 * per-cell mouse handlers, so only one query/popover exists at a time.
 * @param {object} params
 * @param {string | null} params.hoverUrl - URL to fetch HTML from (null disables preview)
 * @param {boolean} [params.isPointerOnCell] - Pointer is currently over the hovered cell
 * @param {boolean} [params.isCellEditing] - When true, hover preview is suppressed
 * @param {() => void} [params.onClosed] - Called when the preview fully closes
 * @returns {UseCellHoverPreviewReturn}
 */
export function useCellHoverPreview({
  hoverUrl,
  isPointerOnCell = false,
  isCellEditing = false,
  onClosed,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [requestedUrl, setRequestedUrl] = useState(/** @type {string | null} */ (null));

  const openTimerRef = useRef(/** @type {ReturnType<typeof setTimeout> | null} */ (null));
  const closeTimerRef = useRef(/** @type {ReturnType<typeof setTimeout> | null} */ (null));
  const isPointerOnPopoverRef = useRef(false);
  const onClosedRef = useRef(onClosed);
  onClosedRef.current = onClosed;

  const {
    data: html,
    isLoading,
    isSuccess,
    isError,
    error,
  } = useCellHoverContent(requestedUrl);

  const showCursorLoader = !!requestedUrl && isLoading;
  const isEmpty = isSuccess && html === null;

  const clearOpenTimer = useCallback(() => {
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
  }, []);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const closePreview = useCallback(() => {
    setIsOpen(false);
    setRequestedUrl(null);
    isPointerOnPopoverRef.current = false;
    onClosedRef.current?.();
  }, []);

  const dismissPreview = useCallback(() => {
    clearOpenTimer();
    clearCloseTimer();
    closePreview();
  }, [clearOpenTimer, clearCloseTimer, closePreview]);

  const onPopoverMouseEnter = useCallback(() => {
    isPointerOnPopoverRef.current = true;
    clearCloseTimer();
  }, [clearCloseTimer]);

  const onPopoverMouseLeave = useCallback(() => {
    isPointerOnPopoverRef.current = false;
    clearCloseTimer();
    closePreview();
  }, [clearCloseTimer, closePreview]);

  const onOpenChange = useCallback((/** @type {boolean} */ nextOpen) => {
    if (!nextOpen) {
      clearOpenTimer();
      clearCloseTimer();
      closePreview();
    }
  }, [clearOpenTimer, clearCloseTimer, closePreview]);

  useEffect(() => {
    if (isCellEditing || !hoverUrl) {
      clearOpenTimer();
      clearCloseTimer();
      if (requestedUrl || isOpen) {
        closePreview();
      }
      return undefined;
    }

    if (isPointerOnCell) {
      clearCloseTimer();
      if (requestedUrl === hoverUrl) {
        return undefined;
      }
      clearOpenTimer();
      openTimerRef.current = setTimeout(() => {
        openTimerRef.current = null;
        setRequestedUrl(hoverUrl);
      }, HOVER_OPEN_DELAY_MS);
      return () => clearOpenTimer();
    }

    clearOpenTimer();
    if (isPointerOnPopoverRef.current) {
      return undefined;
    }

    if (requestedUrl && !isOpen) {
      closePreview();
      return undefined;
    }

    if (!isOpen) return undefined;

    closeTimerRef.current = setTimeout(() => {
      closeTimerRef.current = null;
      if (!isPointerOnPopoverRef.current) {
        closePreview();
      }
    }, HOVER_CLOSE_DELAY_MS);
    return () => clearCloseTimer();
  }, [
    hoverUrl,
    isPointerOnCell,
    isCellEditing,
    requestedUrl,
    isOpen,
    clearOpenTimer,
    clearCloseTimer,
    closePreview,
  ]);

  useEffect(() => {
    if (!requestedUrl || isLoading || isCellEditing) {
      return;
    }

    if (isError) {
      setRequestedUrl(null);
      return;
    }

    setIsOpen(true);
  }, [requestedUrl, isLoading, isCellEditing, isError]);

  return {
    isOpen,
    html: html ?? null,
    isEmpty,
    showCursorLoader,
    error: error ?? null,
    onPopoverMouseEnter,
    onPopoverMouseLeave,
    onOpenChange,
    dismissPreview,
  };
}

export default useCellHoverPreview;
